import React, { useEffect, useState } from "react";
import { CheckCircle, Clock, Eye } from "lucide-react";
import { fetchVendas } from "../../../integration/historico_vendas/fetchVendas";
import { formatToBRL } from "brazilian-values";
import ModalVendas from "../../../components/modals/vendas/Index";
import { BiRefresh } from "react-icons/bi";

const PedidosTabela = () => {
  const [filtros, setFiltros] = useState({
    criadoEm: "",
    id: "",
    cliente: "",
    preco: "",
    meio: "",
    status: ""
  });

  const statusMap = {
    waiting_payment: "Pendente",
    paid: "Pago",
    shipped: "Enviado"
  };

  const [pedidos, setPedidos] = useState([]);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const itensPorPagina = 10;

  const [pedidoAberto, setPedidoAberto] = useState(null);

  const funcVendas = async () => {
    setCarregando(true);
    try {
      const resposta = await fetchVendas({ paginaAtual, itensPorPagina });
      const { dados, total } = resposta;

      setPedidos(dados || []);
      setTotalRegistros(total || 0);
      setTotalPaginas(Math.ceil((total || 0) / itensPorPagina));
    } catch (erro) {
      console.error("Erro ao buscar vendas:", erro);
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    funcVendas();
  }, [paginaAtual]);

  const formatToDateTime = (dataStr) => {
    if (!dataStr) return "-";
    const data = new Date(dataStr);
    return data.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
  };

  const pedidosFiltrados = pedidos.filter((pedido) =>
    Object.entries(filtros).every(([campo, valor]) => {
      if (!valor) return true;
      const dado = pedido[campo];
      if (dado === undefined || dado === null) return false;
      const dadoString = (typeof dado === "number" ? dado.toFixed(2) : dado)
        .toString()
        .toLowerCase();
      return dadoString.includes(valor.toLowerCase());
    })
  );

  const handleVerMais = (pedidoId) => {
    setPedidoAberto(pedidoAberto === pedidoId ? null : pedidoId);
  };

  const closeModal = () => {
    setPedidoAberto(null);
  };

  return (
    <div className="w-full max-w-7xl mx-auto p-6 bg-[#fcfcfd] rounded-2xl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-3xl font-light text-[#0c0c0c]">Vendas</h2>
        <span className="text-gray-500 text-sm">
          Total: <strong>{totalRegistros}</strong> registros
        </span>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <button
          onClick={() => funcVendas()}
          className="inline-flex items-center gap-2 px-5 py-2 bg-[#f5f4f1] text-gray-700 font-medium rounded-lg border border-gray-300 hover:bg-gray-100 transition"
        >
          <BiRefresh className="w-5 h-5" />
          Atualizar Histórico
        </button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm text-gray-700">
          <thead className="bg-[#f5f4f1] text-gray-700 font-semibold">
            <tr>
              {[
                "Criado em",
                "Id",
                "Cliente",
                "Valor",
                "Meio",
                "Status",
                "Ações"
              ].map((title) => (
                <th key={title} className="px-4 py-3 text-left">
                  {title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {carregando ? (
              <tr>
                <td colSpan={7} className="text-center py-4 text-gray-500">
                  Carregando...
                </td>
              </tr>
            ) : pedidosFiltrados.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-4 text-gray-500">
                  Nenhum pedido encontrado.
                </td>
              </tr>
            ) : (
              pedidosFiltrados.map((pedido) => (
                <tr key={pedido.id} className="hover:bg-gray-50 transition">
                  <td className="px-4 py-3">
                    {formatToDateTime(pedido.createdAt)}
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-800">
                    {pedido.supplierTransactionId?.length > 15
                      ? `${pedido.supplierTransactionId.slice(0, 15)}...`
                      : pedido.supplierTransactionId}
                  </td>
                  <td className="px-4 py-3">{pedido.customer?.name || "-"}</td>
                  <td className="px-4 py-3 font-semibold">
                    {formatToBRL(pedido.amount / 100)}
                  </td>
                  <td className="px-4 py-3">{pedido.paymentMethod}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                        pedido.paymentStatus === "paid"
                          ? "bg-green-100 text-green-700"
                          : "bg-yellow-100 text-yellow-700"
                      }`}
                    >
                      {pedido.paymentStatus === "paid" ? (
                        <CheckCircle className="w-4 h-4" />
                      ) : (
                        <Clock className="w-4 h-4" />
                      )}
                      {statusMap[pedido.paymentStatus] || pedido.paymentStatus}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleVerMais(pedido.id)}
                      className="inline-flex items-center gap-1 text-[#9f8d72] hover:text-[#9f8d72] transition text-sm font-medium"
                    >
                      <Eye className="w-4 h-4" />
                      Ver mais
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Paginação */}
      <div className="mt-6 flex justify-between items-center text-sm text-gray-700">
        <div>
          Página <strong>{paginaAtual}</strong> de{" "}
          <strong>{totalPaginas}</strong>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setPaginaAtual(Math.max(1, paginaAtual - 1))}
            disabled={paginaAtual === 1}
            className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300 transition"
          >
            Anterior
          </button>
          <button
            onClick={() =>
              setPaginaAtual(Math.min(totalPaginas, paginaAtual + 1))
            }
            disabled={paginaAtual === totalPaginas}
            className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300 transition"
          >
            Próxima
          </button>
        </div>
      </div>

      {pedidoAberto && (
        <ModalVendas
          data={pedidos.find((p) => p.id === pedidoAberto)}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default PedidosTabela;
